import { Link } from "react-router-dom";
import { useSession } from "@/hooks/useSession";
import { usePatient } from "@/hooks/usePatient";
import { useLabPanels, panelStatusBadge, type LabPanelWithResults } from "@/hooks/useLabPanels";
import { AppLayout } from "@/components/Layout";
import { Card } from "@/components/Card";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingScreen } from "@/components/LoadingScreen";
import { Alert } from "@/components/Alert";

export default function Labs() {
  const { session } = useSession();
  const patientQ = usePatient(session?.user.id);
  const panelsQ = useLabPanels(patientQ.data?.id);

  if (patientQ.isLoading || panelsQ.isLoading) return <LoadingScreen />;

  const panels = panelsQ.data ?? [];

  return (
    <AppLayout>
      <PageHeader
        title="Labs"
        subtitle="Your lab results, ordered chronologically. Tap any panel for the full breakdown."
      />

      {panelsQ.error && (
        <div className="mb-4">
          <Alert tone="error">{(panelsQ.error as Error).message}</Alert>
        </div>
      )}

      {panels.length === 0 ? (
        <EmptyState
          title="No labs yet"
          body="When your clinician orders labs, they'll show up here once results come back. For TRT, expect a baseline panel before treatment starts and a follow-up panel around 90 days in."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {panels.map((p) => <PanelCard key={p.id} panel={p} />)}
        </div>
      )}

      {/* Footer note — helpful framing for TRT patients seeing labs for the first time */}
      {panels.length > 0 && (
        <p className="text-center text-[11.5px] text-fh-gray mt-6">
          Reference ranges shown are the lab's standard population ranges. Your clinician's interpretation considers your individual context — values outside the range are not always concerning.
        </p>
      )}
    </AppLayout>
  );
}

function PanelCard({ panel }: { panel: LabPanelWithResults }) {
  const status = panelStatusBadge(panel.status);
  const dateShown = panel.received_at ?? panel.collected_at ?? panel.ordered_at;
  const dateLabel = panel.received_at
    ? "Results received"
    : panel.collected_at
    ? "Sample collected"
    : "Ordered";

  const hasResults = panel.lab_results.length > 0;
  const outOfRange = panel.out_of_range_count;

  return (
    <Link to={`/labs/${panel.id}`} className="block">
      <Card className="hover:shadow-card hover:border-fh-green-light transition cursor-pointer">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-[18px] text-fh-ink m-0">{panel.panel_name}</h3>
            <div className="text-[12px] text-fh-gray mt-1">
              {dateLabel}: <strong className="text-fh-ink font-semibold">{formatDate(dateShown)}</strong>
              {panel.lab_provider && <> · {panel.lab_provider}</>}
            </div>
          </div>
          <StatusBadge tone={status.tone} label={status.label} />
        </div>

        {hasResults && (
          <div className="flex items-center gap-3 mt-3 text-[12.5px]">
            <span className="text-fh-gray">
              <strong className="text-fh-ink">{panel.lab_results.length}</strong> {panel.lab_results.length === 1 ? "test" : "tests"}
            </span>
            {outOfRange > 0 ? (
              <span className="text-amber-700 font-semibold">
                <strong>{outOfRange}</strong> {outOfRange === 1 ? "value" : "values"} outside reference range
              </span>
            ) : (
              <span className="text-fh-green-dark font-semibold">All values within reference range</span>
            )}
            <span className="ml-auto text-fh-green font-semibold">View results →</span>
          </div>
        )}

        {!hasResults && panel.status === "ordered" && (
          <div className="text-[12.5px] text-fh-gray mt-3">
            Visit your local LabCorp or Quest to have blood drawn. Results typically come back within 1–3 business days.
          </div>
        )}
      </Card>
    </Link>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
